'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { TextField, Popover, Box, Button, Stack, Typography } from '@mui/material';
import { format, subDays, startOfDay, endOfDay, isAfter, isBefore } from 'date-fns';

/**
 * DateRangePicker Component Props
 */
export interface DateRangePickerProps {
  /** Label text displayed above the input */
  label?: string;
  /** Start date (YYYY-MM-DD format) */
  startDate: string;
  /** End date (YYYY-MM-DD format) */
  endDate: string;
  /** Callback when dates change */
  onChange: (startDate: string, endDate: string) => void;
  /** Date format (default: DD/MM/YYYY) */
  format?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Optional CSS class name */
  className?: string;
  /** Show preset buttons */
  showPresets?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Minimum selectable date (YYYY-MM-DD format) */
  minDate?: string;
  /** Maximum selectable date (YYYY-MM-DD format) */
  maxDate?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div<{ $size?: 'sm' | 'md' }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 240px;
  
  /* Size-specific styles */
  ${({ $size }) => $size === 'sm' && `
    .MuiOutlinedInput-root {
      border-radius: 4px !important;
    }
  `}
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  line-height: 1;
`;


// ============================================================================
// MUI Theme Customization
// ============================================================================

const createMuiTheme = (size: 'sm' | 'md' = 'md') => createTheme({
  palette: {
    primary: {
      main: '#FB6F2A',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            border: '1px solid #D9D9DC',
            borderRadius: size === 'sm' ? '4px' : '8px',
            transition: 'all 0.2s ease',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: '#d1d5db',
              '& fieldset': {
                border: 'none',
              },
            },
            '&.Mui-focused': {
              borderColor: '#FB6F2A',
              boxShadow: '0 0 0 3px rgba(251, 111, 42, 0.1)',
              '& fieldset': {
                border: 'none',
              },
            },
          },
          '& .MuiInputBase-input': {
            padding: size === 'sm' ? '5px 16px' : '12px 16px',
            fontSize: '14px',
            fontFamily: "'Menlo', 'Monaco', 'Courier New', monospace",
            color: '#111827',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '8px',
          color: '#6b7280',
          '&:hover': {
            backgroundColor: 'rgba(251, 111, 42, 0.08)',
          },
        },
      },
    },
  },
});

// ============================================================================
// Shortcuts Configuration
// ============================================================================

interface ShortcutConfig {
  label: string;
  getValue: () => [Date, Date];
}

const shortcuts: ShortcutConfig[] = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date();
      return [startOfDay(subDays(today, 6)), endOfDay(today)];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = new Date();
      return [startOfDay(subDays(today, 29)), endOfDay(today)];
    },
  },
  {
    label: 'Last 90 Days',
    getValue: () => {
      const today = new Date();
      return [startOfDay(subDays(today, 89)), endOfDay(today)];
    },
  },
];

// ============================================================================
// Component
// ============================================================================

/**
 * DateRangePicker Component
 * 
 * Custom date range selector using FREE MUI X Date Pickers.
 * Single input field that displays "start date – end date".
 * Popup with shortcuts and dual calendar for range selection.
 * 
 * @component
 * @example
 * ```tsx
 * <DateRangePicker
 *   label="Date Range"
 *   startDate="2025-10-19"
 *   endDate="2025-10-28"
 *   onChange={(start, end) => {
 *     setStartDate(start);
 *     setEndDate(end);
 *   }}
 * />
 * ```
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  startDate,
  endDate,
  onChange,
  disabled = false,
  className,
  size = 'md',
  minDate,
  maxDate,
}) => {
  // Convert string dates to Date objects (parse YYYY-MM-DD as local date, not UTC)
  const parseDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    // Parse YYYY-MM-DD format and create date in local timezone to avoid timezone shifts
    const parts = dateStr?.split('-');
    if (parts?.length !== 3) {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    }
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return null;
    }
    
    // Create date in local timezone (not UTC)
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
  };

  // Convert Date to YYYY-MM-DD string
  const formatDateToString = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  // Format for display (DD/MM/YYYY)
  const formatForDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'dd/MM/yyyy');
  };

  const [startDateValue, setStartDateValue] = useState<Date | null>(parseDate(startDate));
  const [endDateValue, setEndDateValue] = useState<Date | null>(parseDate(endDate));
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = Boolean(anchorEl);

  // Sync internal state with props when they change (e.g., when cleared externally)
  useEffect(() => {
    setStartDateValue(parseDate(startDate));
    setEndDateValue(parseDate(endDate));
  }, [startDate, endDate]);

  // Display value in input
  const displayValue = 
    startDateValue && endDateValue
      ? `${formatForDisplay(startDateValue)} – ${formatForDisplay(endDateValue)}`
      : '';

  // Handle input click to open popover
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setTempStartDate(null);
    }
  };

  // Handle popover close
  const handleClose = () => {
    setAnchorEl(null);
    setTempStartDate(null);
  };

  // Handle date selection
  const handleDateSelect = (date: Date | null) => {
    if (!date) return;
    
    if (!tempStartDate) {
      // First click - select start date
      setTempStartDate(date);
    } else {
      // Second click - select end date
      const start = isBefore(date, tempStartDate) ? date : tempStartDate;
      const end = isAfter(date, tempStartDate) ? date : tempStartDate;
      
      setStartDateValue(start);
      setEndDateValue(end);
      onChange(formatDateToString(start), formatDateToString(end));
      handleClose();
    }
  };

  // Handle shortcut click
  const handleShortcut = (shortcut: ShortcutConfig) => {
    const [start, end] = shortcut.getValue();
    setStartDateValue(start);
    setEndDateValue(end);
    onChange(formatDateToString(start), formatDateToString(end));
    handleClose();
  };

  // Parse min/max dates
  const minDateValue = minDate ? parseDate(minDate) : null;
  const maxDateValue = maxDate ? parseDate(maxDate) : null;
  // Default maxDate to today if not provided
  const effectiveMaxDate = maxDateValue || new Date();

  // Normalize dates to start of day for comparison (to avoid time-of-day issues)
  const normalizeToStartOfDay = (date: Date): Date => {
    return startOfDay(date);
  };

  // Filter shortcuts based on min/max date restrictions
  const availableShortcuts = React.useMemo(() => {
    return shortcuts.filter((shortcut) => {
      const [start, end] = shortcut.getValue();
      
      // Normalize dates for comparison (use start of day for all comparisons)
      // This ensures we compare calendar days, not times
      const normalizedStart = normalizeToStartOfDay(start);
      const normalizedEnd = normalizeToStartOfDay(end); // Normalize endOfDay to startOfDay for comparison
      const normalizedMinDate = minDateValue ? normalizeToStartOfDay(minDateValue) : null;
      const normalizedMaxDate = maxDateValue ? normalizeToStartOfDay(maxDateValue) : null;
      
      // Check if shortcut range is completely within allowed range
      // Start date must be >= minDate
      if (normalizedMinDate) {
        const startTime = normalizedStart.getTime();
        const minTime = normalizedMinDate.getTime();
        if (startTime < minTime) {
          return false;
        }
      }
      // End date must be <= maxDate
      // Since we normalized both to startOfDay, we compare calendar days
      // For end dates, we need to check if the end date (normalized to start of day)
      // is <= maxDate (also normalized to start of day)
      if (normalizedMaxDate) {
        const endTime = normalizedEnd.getTime();
        const maxTime = normalizedMaxDate.getTime();
        // Allow equal dates (when shortcut ends on maxDate)
        if (endTime > maxTime) {
          return false;
        }
      }
      return true;
    });
  }, [minDateValue, maxDateValue]);
  // Check if date is in range (for selection in progress)
  const isInRange = (date: Date): boolean => {
    if (!tempStartDate) return false;
    const start = tempStartDate;
    return (isAfter(date, start) || date.getTime() === start.getTime()) && isBefore(date, effectiveMaxDate);
  };

  // Check if date is within the selected range (for highlighting)
  const isInSelectedRange = (date: Date): boolean => {
    if (!startDateValue || !endDateValue) return false;
    const dateTime = date.getTime();
    const startTime = startDateValue.getTime();
    const endTime = endDateValue.getTime();
    // Check if date is between start and end (inclusive)
    return dateTime >= startTime && dateTime <= endTime;
  };

  const muiTheme = createMuiTheme(size);

  return (
    <ThemeProvider theme={muiTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Container className={className} $size={size}>
          {label && <Label>{label}</Label>}
          <TextField
            ref={inputRef}
            value={displayValue}
            onClick={handleClick}
            placeholder="DD/MM/YYYY – DD/MM/YYYY"
            size="small"
            disabled={disabled}
            fullWidth
            InputProps={{
              readOnly: true,
              style: { cursor: 'pointer' },
            }}
          />
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  marginTop: '4px',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', p: 2 }}>
              {/* Shortcuts */}
              {availableShortcuts?.length > 0 && (
                <Stack spacing={1} sx={{ pr: 2, borderRight: '1px solid #e5e7eb' }}>
                  {availableShortcuts.map((shortcut) => (
                    <Button
                      key={shortcut.label}
                      onClick={() => handleShortcut(shortcut)}
                      variant="text"
                      size="small"
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        color: '#374151',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      {shortcut.label}
                    </Button>
                  ))}
                </Stack>
              )}

              {/* Calendars */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Start Date Calendar */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151',
                      marginBottom: '8px',
                      paddingLeft: '12px',
                    }}
                  >
                    Start Date
                  </Typography>
                    <DateCalendar
                      value={tempStartDate || startDateValue}
                      onChange={handleDateSelect}
                      minDate={minDateValue || undefined}
                      maxDate={effectiveMaxDate}
                      slotProps={{
                        day: (ownerState) => {
                          const day = ownerState.day;
                          const isInRange = startDateValue && endDateValue && 
                            day.getTime() >= startDateValue.getTime() && 
                            day.getTime() <= endDateValue.getTime() &&
                            day.getTime() !== startDateValue.getTime() &&
                            day.getTime() !== endDateValue.getTime();
                          
                          const isStartDate = day.getTime() === startDateValue?.getTime();
                          const isEndDate = day.getTime() === endDateValue?.getTime();

    const isTempStart =
    tempStartDate && day.getTime() === tempStartDate.getTime();
                          
                          return {
                            sx: {
                              ...(isTempStart && {
                                backgroundColor: '#FB6F2A !important',
                                background: '#FB6F2A !important',
                                '&:hover': {
                                  backgroundColor: '#FFD8C2',
                                },
                              }),
                              ...(isInRange && {
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                },
                              }),
                              ...(isStartDate && {
                                backgroundColor: '#FB6F2A',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#FB6F2A',
                                },
                              }),
                              ...(isEndDate && {
                                backgroundColor: '#FB6F2A',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#FB6F2A',
                                },
                              }),
                            },
                          };
                        },
                      }}
                      sx={{
                        '& .MuiPickersDay-root': {
                          '&.Mui-selected': {
                            backgroundColor: '#FB6F2A',
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* End Date Calendar */}
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#374151',
                        marginBottom: '8px',
                        paddingLeft: '12px',
                      }}
                    >
                      End Date
                    </Typography>
                    <DateCalendar
                      value={endDateValue}
                      onChange={handleDateSelect}
                      minDate={minDateValue || undefined}
                      maxDate={effectiveMaxDate}
                      slotProps={{
                        day: (ownerState) => {
                          const day = ownerState.day;
                          const isInRange = startDateValue && endDateValue && 
                            day.getTime() >= startDateValue.getTime() && 
                            day.getTime() <= endDateValue.getTime() &&
                            day.getTime() !== startDateValue.getTime() &&
                            day.getTime() !== endDateValue.getTime();
                          
                          const isStartDate = day.getTime() === startDateValue?.getTime();
                          const isEndDate = day.getTime() === endDateValue?.getTime();
                          
                          return {
                            sx: {
                              ...(isInRange && {
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                '&:hover': {
                                  backgroundColor: 'rgba(59, 130, 246, 0.15)',
                                },
                              }),
                              ...(isStartDate && {
                                backgroundColor: '#FB6F2A',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#FB6F2A',
                                },
                              }),
                              ...(isEndDate && {
                                backgroundColor: '#FB6F2A',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#FB6F2A',
                                },
                              }),
                            },
                          };
                        },
                      }}
                      sx={{
                        '& .MuiPickersDay-root': {
                          '&.Mui-selected': {
                            backgroundColor: '#FB6F2A',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
            </Box>
          </Popover>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateRangePicker;
